import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { SuggestedField } from "components/ArtificialIntelligence/SuggestedField";
import { SuggestedStatePicker } from "components/ArtificialIntelligence/SuggestedStatePicker";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { useRevealOnTravel, useSelectOnKeyboardFocus } from "components/Document/ReviewInteraction";
import { FacilityPicker } from "components/FacilityPicker";
import { fieldAccessorySlot } from "components/Forms/FieldAccessory";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { unrecognizedOption } from "components/Forms/unrecognizedOption";
import { RcraInfoLink } from "components/RcraInfoLink";
import { UnifiedTagsInput } from "components/UnifiedTagsInput";
import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  EMANIFEST_WASTE_UNIT_LABELS,
  normalizeEManifestWasteUnit,
} from "encamp-shared/src/waste/eManifestWasteUnit";
import { gql } from "generated-graphql";
import {
  FacilityPickerFragment,
  FacilitySuggestionSource,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { TAGS_QUERY } from "queries/tags";
import { memo, useCallback, useMemo, useState } from "react";
import { Controller, useWatch } from "react-hook-form";

import type { useManifestReviewSection } from "../hooks/useManifestReviewSection";

import { isTexasGenerator, splitEpaIdAndRegistration } from "./epaIdRegistrationSplit";
import { GeneratorSiteAddressFields } from "./GeneratorSiteAddressFields";
import { NO_ROWS_WITH_OFFERS, type RowsWithOffers } from "./manifestSourceOffers";
import { reviewDiffAdornment } from "./reviewDiffAdornment";
import {
  type ReviewFieldDiff,
  type ReviewFieldDiffIndex,
  wasteLineDiffKey,
} from "./reviewFieldDiffs";
import { useRowExpansion } from "./rowExpansion";

/** An empty index, so callers that don't supply diffs need no null checks. */
const EMPTY_DIFF_INDEX: ReviewFieldDiffIndex = {
  manifestFieldDiffs: new Map(),
  wasteLineFieldDiffs: new Map(),
};

type ManifestDetailSectionProps = {
  manifest: ReturnType<typeof useManifestReviewSection>;
  /** Review-time per-field diff verdicts, indexed by review form field name. */
  fieldDiffs?: ReviewFieldDiffIndex;
  /** Source pillars physically present on the manifest (includes the unreviewed document). */
  availableSources?: WasteManifestSource[];
  /** Repeated rows some source holds a value for, so they open without being asked. */
  rowsWithOffers?: RowsWithOffers;
};

type ManifestSection = ReturnType<typeof useManifestReviewSection>;

/** Shared, so an omitted prop is the same value every render. */
const NO_SOURCES: WasteManifestSource[] = [];

/**
 * Let travel open this row before it lands inside it.
 *
 * A collapsed row keeps its fields in the DOM but unfocusable, so without this
 * "jump to next" into one of them scrolls, reports an arrival, and leaves the
 * caret exactly where it was — and the next jump then counts from a field the
 * reviewer never reached.
 */
function useRowReveal(
  group: "wasteLines" | "transporters",
  index: number,
  onToggle: (expanded: boolean) => void
): void {
  useRevealOnTravel(
    `${group}.${index}`,
    (path) => path.startsWith(`${group}.${index}.`),
    () => onToggle(true)
  );
}

/** The slice of the manifest section the facility picker/suggestion needs. */
type FacilitySectionManifest = Pick<
  ManifestSection,
  | "facility"
  | "suggestedFacility"
  | "suggestedFacilitySource"
  | "manifestControl"
  | "manifestSetValue"
>;

/**
 * Deep-link beneath an EPA ID field to look that exact site up in RCRAInfo.
 * Hidden until the field holds a value — an empty field has nothing to look up,
 * and the section's general "Search RCRAInfo HWIP" link covers look-ups by name.
 *
 * It runs a site search for the ID (not a direct record link), so an
 * unrecognized ID shows a graceful "no site found" results page. Confirming an
 * ID against RCRAInfo handler data in-app is a separate, server-side enhancement.
 */
function EpaIdLookup({ epaId }: { epaId: unknown }) {
  if (typeof epaId !== "string" || epaId.trim() === "") return null;
  return (
    <Box sx={{ pl: 0.5 }}>
      <RcraInfoLink epaId={epaId} />
    </Box>
  );
}

const MANIFEST_REVIEW_VENDOR_NAMES = gql(`
  query ManifestReviewVendorNames {
    wasteManifestVendors
  }
`);

/**
 * Vendor that managed the shipment. Not an EPA form field and never extracted
 * from the document — a reviewer picks it so vendors who only ever send
 * manifests still roll up under their name on the manifest grids and the
 * tenant's vendor filter. Free-text as well as picked, since the canonical
 * vendor list only covers vendors we already ingest data from.
 */
function VendorSection({ manifest }: { manifest: Pick<ManifestSection, "manifestControl"> }) {
  const { data } = useQuery(MANIFEST_REVIEW_VENDOR_NAMES);

  const vendorItems = useMemo(
    () =>
      (data?.wasteManifestVendors ?? [])
        .map((name) => name.trim())
        .filter((name) => name.length > 0)
        .map((name) => ({ value: name, label: name })),
    [data?.wasteManifestVendors]
  );

  return (
    <FormAutocomplete
      name="vendorSource"
      label="Vendor"
      control={manifest.manifestControl}
      autocompleteItems={vendorItems}
      autocompleteProps={{ freeSolo: true, size: "small" }}
    />
  );
}

/**
 * Offer to separate a state registration number the form printed inside Box 1.
 *
 * Texas manifests write the TCEQ registration straight after the EPA ID, so the
 * combined value fails EPA ID validation and the reviewer is shown an error for
 * a document that is correct. The fix is a split, not a correction, and the form
 * already has the field to put the second half in.
 *
 * An offer rather than an automatic split: the reviewer is here because the
 * document is hard to read, and silently rewriting the one field they are being
 * warned about would be the worst moment to be wrong.
 */
function EpaIdSplitOffer({ manifest }: { manifest: ManifestSection }) {
  const control = manifest.manifestControl;
  const generatorId = useWatch({ control, name: "generatorId" });
  const mailState = useWatch({ control, name: "generatorMailState" });
  const locationState = useWatch({ control, name: "generatorLocationState" });
  const existingSwr = useWatch({
    control,
    name: "generatorTxStateWasteRegistrationId",
  });
  const split = splitEpaIdAndRegistration(generatorId);

  // The precondition lives here rather than at the call site, so the offer
  // cannot be rendered somewhere its only destination field does not apply.
  if (!isTexasGenerator({ epaId: generatorId, mailState, locationState })) return null;

  // Nothing to offer once the registration is already recorded — the reviewer
  // would be re-answering a question they have answered.
  if (split == null || existingSwr?.trim()) return null;

  const applySplit = () => {
    manifest.manifestSetValue("generatorId", split.epaId, { shouldDirty: true });
    manifest.manifestSetValue("generatorTxStateWasteRegistrationId", split.registrationNumber, {
      shouldDirty: true,
    });
  };

  return (
    <Alert
      severity="info"
      variant="outlined"
      action={
        <Button size="small" onClick={applySplit}>
          Split
        </Button>
      }
    >
      This looks like an EPA ID with a state registration number after it. Box 1 has no separate
      field for it on the federal form, so it is often written here. Split into{" "}
      <strong>{split.epaId}</strong> and <strong>{split.registrationNumber}</strong>?
    </Alert>
  );
}

/**
 * Facility association for the manifest. Editable via the standard waste facility
 * picker, seeded from the confirmed WasteManifest.facilityId. When Document
 * Intelligence produced an unconfirmed match that differs from the current
 * value, it's offered as an accept-able suggestion with its provenance — never
 * pre-filled, so confirming (or a partial save) can't silently adopt a weak
 * match. The link to the facility's documents is created on confirm, in the
 * resolver.
 */
export function FacilitySection({ manifest }: { manifest: FacilitySectionManifest }) {
  const suggestion = manifest.suggestedFacility;
  const confirmedFacility = manifest.facility;
  const currentFacilityId = useWatch({ control: manifest.manifestControl, name: "facilityId" });
  const showSuggestion = !!suggestion && suggestion.id !== currentFacilityId;

  // "Use suggestion" writes the id straight to the form, bypassing the picker's
  // onChange cache — so stash the accepted suggestion fragment here to seed the
  // picker's display. Without it the picker falls back to the bare id and the
  // waste-variant query re-hydrates it (a flash for waste-enabled facilities,
  // blank for facilities the waste query doesn't return).
  const [acceptedSuggestion, setAcceptedSuggestion] = useState<FacilityPickerFragment | null>(null);

  const provenance =
    manifest.suggestedFacilitySource === FacilitySuggestionSource.GeneratorEpaId
      ? "Matched by generator EPA ID"
      : "Suggested by address match";

  // Fragments we can render without a server round-trip: the confirmed facility
  // and any suggestion the reviewer has accepted. Freshly-picked values aren't
  // here — the picker caches those itself from onChange.
  const knownFacilities: (FacilityPickerFragment | null)[] = [
    confirmedFacility && {
      id: confirmedFacility.id,
      name: confirmedFacility.name,
      streetAddress1: confirmedFacility.streetAddress1 ?? null,
    },
    acceptedSuggestion,
  ];

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setAcceptedSuggestion({ id: suggestion.id, name: suggestion.name, streetAddress1: null });
    manifest.manifestSetValue("facilityId", suggestion.id, { shouldDirty: true });
  };

  return (
    <Stack spacing={1}>
      <Controller
        name="facilityId"
        control={manifest.manifestControl}
        render={({ field, fieldState }) => {
          const known = knownFacilities.find((f) => f?.id === field.value);
          return (
            <FacilityPicker
              {...fieldState}
              facilityQueryVariant="waste"
              label="Facility"
              // A known full fragment renders its name immediately; otherwise
              // fall back to the bare id for the picker to hydrate/match.
              value={known ?? (field.value || null)}
              showErrors={false}
              onChange={(facility) => field.onChange(facility?.id ?? "")}
            />
          );
        }}
      />
      {showSuggestion && (
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button size="small" onClick={acceptSuggestion}>
              Use suggestion
            </Button>
          }
        >
          Suggested facility: {suggestion.name} — {provenance}
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Memoized, which is the point of `useManifestReviewSection` handing back a
 * stable object.
 *
 * The dialog around this re-renders for things the form has no stake in — the
 * caret moving to another field, a validation pass landing — and rendering
 * sixty fields costs a fifth of a second. Skipping those renders is why
 * clicking a field feels immediate.
 */
function ManifestDetailSectionInner({
  manifest,
  fieldDiffs = EMPTY_DIFF_INDEX,
  availableSources = NO_SOURCES,
  rowsWithOffers = NO_ROWS_WITH_OFFERS,
}: ManifestDetailSectionProps) {
  // Tabbing into a field selects what it holds, so transcribing over a poor
  // reading is one keystroke per field rather than a clear-then-type. On the
  // container because the section renders around sixty inputs through shared
  // controls, and a field added later should not have to opt in.
  const selectOnFocus = useSelectOnKeyboardFocus();
  // "First time we've seen this manifest": the document is the only source, so
  // there is nothing to validate the parsed values against. Warn the reviewer to
  // check carefully rather than leaning on cross-source agreement.
  const isFirstTimeManifest =
    availableSources.length === 1 && availableSources[0] === WasteManifestSource.ManifestDocument;
  const manifestFieldDiffs = fieldDiffs.manifestFieldDiffs;
  // TX Solid Waste Registration is Texas-only and frequently absent even there,
  // so the field stays hidden unless this looks like a Texas generator. We treat
  // it as Texas when either generator state (mailing OR site) is TX, when the
  // generator EPA ID carries a TX prefix (covers manifests whose site address is
  // "SAME"/blank), or when a SWR value is already present.
  const control = manifest.manifestControl;
  const generatorMailState = useWatch({ control, name: "generatorMailState" });
  const generatorLocationState = useWatch({ control, name: "generatorLocationState" });
  const generatorIdValue = useWatch({ control, name: "generatorId" });
  const txSwrValue = useWatch({ control, name: "generatorTxStateWasteRegistrationId" });
  const showTxSwr =
    isTexasGenerator({
      epaId: generatorIdValue,
      mailState: generatorMailState,
      locationState: generatorLocationState,
    }) || !!txSwrValue?.trim();

  if (manifest.manifestDataLoaded) {
    return (
      <Box sx={{ p: 1 }} {...selectOnFocus}>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {manifest.manifestNeedsReview ? (
              <>
                AI read this manifest document. Please check the values it read{" "}
                <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                  (marked with <SparkleIcon fontSize="inherit" sx={{ verticalAlign: "middle" }} />)
                </Box>{" "}
                and confirm.
              </>
            ) : (
              "These are the values read from the manifest document. Saving an edit here updates the manifest and re-runs its compliance checks."
            )}{" "}
            Need to confirm an EPA ID? <RcraInfoLink />
          </Typography>

          {isFirstTimeManifest && (
            <Alert severity="warning">
              This is the first time we&apos;ve seen this manifest and we don&apos;t have any other
              data to validate against. Review carefully.
            </Alert>
          )}

          {/* Fields follow the EPA Form 8700-22 box order, top to bottom, so a
              reviewer can check parsed values straight down the document. Box
              numbers are shown in the labels/headers. Line Items and
              Transporters stay grouped since they continue onto page 2. */}
          <FacilitySection manifest={manifest} />

          {/* Encamp associations rather than form boxes, so they sit above the
              transcription fields with the facility picker. */}
          <VendorSection manifest={manifest} />

          {/* Box 1 — Generator EPA ID, with the Texas SWR alongside it for TX
              generators / when a value exists (the EPA ID grows to fill the row
              otherwise). Kept in one persistent Stack so the input never
              remounts mid-typing. A RCRAInfo lookup link sits below for manual
              confirmation, in addition to the inline validation warning. */}
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2}>
              <SuggestedTextField
                name="generatorId"
                control={manifest.manifestControl}
                needsReview={manifest.manifestNeedsReview}
                label="1. Generator ID Number"
                size="small"
                sx={{ flex: 2, minWidth: 0 }}
                slotProps={{
                  input: {
                    endAdornment: reviewDiffAdornment(manifestFieldDiffs.get("generatorId")),
                  },
                }}
              />
              {showTxSwr && (
                <SuggestedTextField
                  name="generatorTxStateWasteRegistrationId"
                  control={manifest.manifestControl}
                  needsReview={manifest.manifestNeedsReview}
                  label="1. TX SWR Number"
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  helperText="Texas only (optional)"
                />
              )}
            </Stack>
            <EpaIdSplitOffer manifest={manifest} />
            <EpaIdLookup epaId={generatorIdValue} />
          </Stack>

          {/* Box 4 */}
          <SuggestedTextField
            name="manifestTrackingNumber"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="4. Manifest Tracking Number"
            size="small"
            fullWidth
          />
          {manifest.trackingNumberChanged && (
            <Alert severity="warning" variant="outlined">
              Changing the tracking number will move this manifest data to a different processed
              manifest record.
            </Alert>
          )}

          {/* Box 5 */}
          <Typography variant="subtitle1" fontWeight="bold">
            5. Generator
          </Typography>
          <SuggestedTextField
            name="generatorName"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Name"
            size="small"
            fullWidth
            slotProps={{
              input: { endAdornment: reviewDiffAdornment(manifestFieldDiffs.get("generatorName")) },
            }}
          />
          <SuggestedTextField
            name="generatorMailStreet1"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street"
            size="small"
            fullWidth
          />
          <SuggestedTextField
            name="generatorMailStreet2"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="Mailing Street 2"
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <SuggestedTextField
              name="generatorMailCity"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="City"
              size="small"
              sx={{ flex: 2, minWidth: 0 }}
            />
            <SuggestedStatePicker
              name="generatorMailState"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              sx={{ flex: 1, minWidth: 56 }}
            />
            <SuggestedTextField
              name="generatorMailZip"
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label="Zip"
              size="small"
              sx={{ flex: 1.5, minWidth: 0 }}
            />
          </Stack>
          <GeneratorSiteAddressFields
            control={manifest.manifestControl}
            setValue={manifest.manifestSetValue}
            needsReview={manifest.manifestNeedsReview}
            siteInitiallyBlank={manifest.generatorSiteAddressInitiallyBlank}
            mailingPopulated={manifest.generatorMailingAddressPresent}
            fieldDiffs={manifestFieldDiffs}
          />

          {/* Boxes 6 & 7 (+ continuation) */}
          <TransportersSection manifest={manifest} rowsWithOffers={rowsWithOffers.transporters} />

          {/* Box 8 */}
          <DesignatedFacilitySection manifest={manifest} fieldDiffs={manifestFieldDiffs} />

          {/* Boxes 9–13 (+ continuation) */}
          <WasteLinesSection
            manifest={manifest}
            wasteLineFieldDiffs={fieldDiffs.wasteLineFieldDiffs}
            rowsWithOffers={rowsWithOffers.wasteLines}
          />

          {/* Box 15 — Generator's certification date. */}
          <SuggestedTextField
            name="shippedDate"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="15. Date Shipped"
            type="date"
            size="small"
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
              input: { endAdornment: reviewDiffAdornment(manifestFieldDiffs.get("shippedDate")) },
            }}
          />

          {/* Box 18 — Discrepancy Indication Space. */}
          <Box18Section manifest={manifest} />

          {/* Box 20 — Designated facility owner/operator certification date. */}
          <SuggestedTextField
            name="receivedDate"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="20. Date Received"
            type="date"
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Box>
    );
  }

  if (manifest.manifestLoading) {
    return <Skeleton variant="rectangular" height={200} sx={{ my: 2, gridColumn: "1 / -1" }} />;
  }

  return (
    <Alert severity="info" variant="outlined" sx={{ my: 2, gridColumn: "1 / -1" }}>
      This document has been classified as a waste manifest. Manifest data will be parsed after you
      confirm, and you can review it from the Waste Manifests page.
    </Alert>
  );
}

/** Box 8 — the TSDF the shipment was consigned to. */
function DesignatedFacilitySection({
  manifest,
  fieldDiffs,
}: {
  manifest: ManifestSection;
  fieldDiffs: Map<string, ReviewFieldDiff>;
}) {
  const desFacilityId = useWatch({ control: manifest.manifestControl, name: "desFacilityId" });

  return (
    <>
      <Typography variant="subtitle1" fontWeight="bold">
        8. Designated Facility
      </Typography>
      <SuggestedTextField
        name="desFacilityName"
        control={manifest.manifestControl}
        needsReview={manifest.manifestNeedsReview}
        label="Name"
        size="small"
        fullWidth
      />
      <Stack spacing={0.5}>
        <SuggestedTextField
          name="desFacilityId"
          control={manifest.manifestControl}
          needsReview={manifest.manifestNeedsReview}
          label="EPA ID"
          size="small"
          fullWidth
          slotProps={{
            input: { endAdornment: reviewDiffAdornment(fieldDiffs.get("desFacilityId")) },
          }}
        />
        <EpaIdLookup epaId={desFacilityId} />
      </Stack>
      <SuggestedTextField
        name="desFacLocationStreet1"
        control={manifest.manifestControl}
        needsReview={manifest.manifestNeedsReview}
        label="Street"
        size="small"
        fullWidth
      />
      <Stack direction="row" spacing={2}>
        <SuggestedTextField
          name="desFacLocationCity"
          control={manifest.manifestControl}
          needsReview={manifest.manifestNeedsReview}
          label="City"
          size="small"
          sx={{ flex: 2, minWidth: 0 }}
        />
        <SuggestedStatePicker
          name="desFacLocationState"
          control={manifest.manifestControl}
          needsReview={manifest.manifestNeedsReview}
          sx={{ flex: 1, minWidth: 56 }}
        />
        <SuggestedTextField
          name="desFacLocationZip"
          control={manifest.manifestControl}
          needsReview={manifest.manifestNeedsReview}
          label="Zip"
          size="small"
          sx={{ flex: 1.5, minWidth: 0 }}
        />
      </Stack>
    </>
  );
}

function WasteLinesSection({
  manifest,
  wasteLineFieldDiffs,
  rowsWithOffers,
}: {
  manifest: ManifestSection;
  wasteLineFieldDiffs: Map<string, ReviewFieldDiff>;
  rowsWithOffers: ReadonlySet<string>;
}) {
  const fieldArray = manifest.wasteLineFieldArray;

  // Keyed by wasteLineNumber rather than field.id, so the "Add Waste Line"
  // handler can open the new row in the same gesture — it knows the number
  // synchronously, where the field id only exists once the append has landed.
  const lineNumbers = useMemo(
    () => fieldArray.fields.map((f) => f.wasteLineNumber),
    [fieldArray.fields]
  );
  // Nothing on an unreviewed manifest has been checked yet, so every line is a
  // transcription target and starts open. On a reviewed one, only the lines some
  // source still holds a value for.
  const { manifestNeedsReview } = manifest;
  const openByDefault = useCallback(
    (lineNumber: string) => manifestNeedsReview || rowsWithOffers.has(lineNumber),
    [manifestNeedsReview, rowsWithOffers]
  );
  const {
    isExpanded,
    setExpanded: setLineExpanded,
    allExpanded,
    toggleAll,
  } = useRowExpansion(lineNumbers, openByDefault);

  const handleAddLine = () => {
    const nextLineNumber = String(
      Math.max(0, ...fieldArray.fields.map((f) => safeParseInt(f.wasteLineNumber) ?? 0)) + 1
    );
    fieldArray.append({
      wasteLineNumber: nextLineNumber,
      dotHazardous: false,
      dotIdNumberDescription: "",
      dotPrintedInformation: "",
      nonHazWasteDescription: "",
      containerNumber: null,
      containerTypeCode: "",
      quantity: null,
      qtyUnitOfMeasureCode: "",
      managementMethodCode: "",
      federalWasteCodes: "",
      stateWasteCodes: "",
      tags: [],
    });
    // Newly-added line starts expanded so the user can fill it in.
    setLineExpanded(nextLineNumber, true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          9–13. Line Items ({fieldArray.fields.length})
        </Typography>
        {fieldArray.fields.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      {fieldArray.fields.map((field, index) => (
        <WasteLineRow
          key={field.id}
          field={field}
          index={index}
          manifest={manifest}
          wasteLineFieldDiffs={wasteLineFieldDiffs}
          expanded={isExpanded(field.wasteLineNumber)}
          onToggle={(open) => setLineExpanded(field.wasteLineNumber, open)}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={handleAddLine}>
        Add Waste Line
      </Button>
    </>
  );
}

type WasteLineRowProps = {
  field: { id: string; wasteLineNumber: string };
  index: number;
  manifest: ManifestSection;
  wasteLineFieldDiffs: Map<string, ReviewFieldDiff>;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
};

// EPA Form 8700-22 holds four waste lines; the fifth onward continue on the
// 8700-22A continuation sheet, which numbers the same fields differently.
const MAIN_FORM_WASTE_LINE_COUNT = 4;
const MAIN_FORM_BOXES = {
  hm: "9a",
  description: "9b",
  containers: "10",
  quantity: "11",
  unit: "12",
  wasteCodes: "13",
  managementMethod: "19",
};
const CONTINUATION_BOXES = {
  hm: "27a",
  description: "27b",
  containers: "28",
  quantity: "29",
  unit: "30",
  wasteCodes: "31",
  managementMethod: "36",
};
type WasteLineBoxNumbers = typeof MAIN_FORM_BOXES | typeof CONTINUATION_BOXES;

/**
 * Box 12's unit of measure, as a select that can still show a code it does not
 * recognise.
 *
 * Its own component because that recognition rule is the only interesting thing
 * on the field, and inline it was the largest thing in an already-long row.
 */
function WasteLineUnitField({
  manifest,
  index,
  label,
}: {
  manifest: ManifestSection;
  index: number;
  label: string;
}) {
  return (
    <SuggestedField
      name={`wasteLines.${index}.qtyUnitOfMeasureCode`}
      control={manifest.manifestControl}
      needsReview={manifest.manifestNeedsReview}
      label={label}
    >
      {({ field, isSuggested, sparkleAdornment, accessory }) => {
        const raw = typeof field.value === "string" ? field.value : null;
        const known = normalizeEManifestWasteUnit(raw);
        // A code the list does not carry stays selectable rather than
        // disappearing: `normalizeEManifestWasteUnit` returning null used to leave
        // the control showing "Select unit…" while the form still held the value,
        // so the reviewer saw a blank and a code they never agreed to went to the
        // mutation.
        const unrecognized = unrecognizedOption(
          raw,
          (value) => normalizeEManifestWasteUnit(value) != null
        );
        const slot = fieldAccessorySlot(
          accessory,
          unrecognized != null ? "Not one of the expected units — please confirm" : undefined
        );
        return (
          <TextField
            {...field}
            select
            value={known ?? unrecognized ?? ""}
            label={label}
            size="small"
            sx={{ flex: 1, minWidth: 90 }}
            error={unrecognized != null}
            helperText={slot.helperText}
            slotProps={{
              formHelperText: slot.formHelperText,
              input: { startAdornment: isSuggested ? sparkleAdornment : undefined },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select unit…</em>
            </MenuItem>
            {unrecognized != null && <MenuItem value={unrecognized}>{unrecognized}</MenuItem>}
            {Object.entries(EMANIFEST_WASTE_UNIT_LABELS).map(([code, optionLabel]) => (
              <MenuItem key={code} value={code}>
                {`${code} — ${optionLabel}`}
              </MenuItem>
            ))}
          </TextField>
        );
      }}
    </SuggestedField>
  );
}

function WasteLineRow({
  field,
  index,
  manifest,
  wasteLineFieldDiffs,
  expanded,
  onToggle,
}: WasteLineRowProps) {
  useRowReveal("wasteLines", index, onToggle);

  /**
   * `compute`, not a watch on the row.
   *
   * Watching `wasteLines.N` hands back the row's whole object, and
   * react-hook-form sets state with it on every change anywhere in the form.
   * React bails out of a `setState` that passes an equal value, which is why
   * watching a single string field costs nothing — but a freshly built object
   * is never equal, so every keystroke in any field re-rendered every waste
   * line in full. `compute` puts the comparison on the two values the header
   * actually shows, and the row re-renders only when one of them changes.
   */
  const { isHazardous, description } = useWatch({
    control: manifest.manifestControl,
    compute: (values) => {
      const line = values.wasteLines?.[index];
      const hazardous = line?.dotHazardous ?? null;
      return {
        isHazardous: hazardous,
        description: hazardous ? line?.dotPrintedInformation : line?.nonHazWasteDescription,
      };
    },
  });
  // A line's box numbers depend on which physical page it lands on.
  const box = index < MAIN_FORM_WASTE_LINE_COUNT ? MAIN_FORM_BOXES : CONTINUATION_BOXES;
  // Diff verdict for one of this line's review fields, keyed by the manifest's
  // stable waste line number.
  const lineDiff = (reviewField: string): ReviewFieldDiff | undefined =>
    wasteLineFieldDiffs.get(wasteLineDiffKey(field.wasteLineNumber, reviewField));

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => onToggle(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Line {field.wasteLineNumber}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {" — "}
              {description}
            </Typography>
          )}
          {!description && <Box flex={1} />}
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              manifest.onDeleteWasteLine(field.wasteLineNumber);
              manifest.wasteLineFieldArray.remove(index);
            }}
            title="Remove waste line"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <SuggestedField
              name={`wasteLines.${index}.dotHazardous`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleIcon, accessory }) => (
                <Stack>
                  <Stack direction="row" alignItems="center">
                    <FormControlLabel
                      control={<Checkbox checked={!!f.value} onChange={f.onChange} />}
                      label={`${box.hm}. DOT Hazardous`}
                    />
                    {isSuggested && sparkleIcon}
                  </Stack>
                  {accessory}
                </Stack>
              )}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.dotIdNumberDescription`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box.description}. DOT ID / Description`}
              size="small"
              fullWidth
            />
          </Stack>
          <SuggestedTextField
            name={`wasteLines.${index}.dotPrintedInformation`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.description}. DOT Printed Information`}
            size="small"
            fullWidth
            multiline
            rows={3}
            slotProps={{
              input: { endAdornment: reviewDiffAdornment(lineDiff("dotPrintedInformation")) },
            }}
          />
          <SuggestedTextField
            name={`wasteLines.${index}.nonHazWasteDescription`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box.description}. Non-Haz Waste Description`}
            size="small"
            fullWidth
            multiline
            rows={3}
            slotProps={{
              input: { endAdornment: reviewDiffAdornment(lineDiff("nonHazWasteDescription")) },
            }}
          />
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.containerNumber`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment, accessory }) => {
                const slot = fieldAccessorySlot(accessory, undefined);
                return (
                  <TextField
                    {...f}
                    value={f.value ?? ""}
                    onChange={(e) =>
                      f.onChange(e.target.value === "" ? null : safeParseInt(e.target.value))
                    }
                    label={`${box.containers}. No. of Containers`}
                    type="number"
                    size="small"
                    sx={{ flex: 1, minWidth: 90 }}
                    helperText={slot.helperText}
                    slotProps={{
                      formHelperText: slot.formHelperText,
                      input: {
                        startAdornment: isSuggested ? sparkleAdornment : undefined,
                      },
                    }}
                  />
                );
              }}
            </SuggestedField>
            <SuggestedTextField
              name={`wasteLines.${index}.containerTypeCode`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box.containers}. Container Type`}
              size="small"
              sx={{ flex: 1, minWidth: 110 }}
              slotProps={{
                input: { endAdornment: reviewDiffAdornment(lineDiff("containerTypeCode")) },
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <SuggestedField
              name={`wasteLines.${index}.quantity`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
            >
              {({ field: f, isSuggested, sparkleAdornment, accessory }) => {
                const slot = fieldAccessorySlot(accessory, undefined);
                return (
                  <TextField
                    {...f}
                    value={f.value ?? ""}
                    onChange={(e) =>
                      f.onChange(e.target.value === "" ? null : safeParseFloat(e.target.value))
                    }
                    label={`${box.quantity}. Total Quantity`}
                    type="number"
                    size="small"
                    sx={{ flex: 1, minWidth: 90 }}
                    helperText={slot.helperText}
                    slotProps={{
                      formHelperText: slot.formHelperText,
                      input: {
                        startAdornment: isSuggested ? sparkleAdornment : undefined,
                        endAdornment: reviewDiffAdornment(lineDiff("quantity")),
                      },
                    }}
                  />
                );
              }}
            </SuggestedField>
            <WasteLineUnitField
              manifest={manifest}
              index={index}
              label={`${box.unit}. Unit Wt./Vol.`}
            />
          </Stack>
          <WasteLineCodesAndTags index={index} manifest={manifest} box={box} lineDiff={lineDiff} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * The tail of a waste line: waste codes, management method, and the line's
 * tags. Management method codes sit at the bottom of the form, so they come
 * after the waste codes within each line.
 */
function WasteLineCodesAndTags({
  index,
  manifest,
  box,
  lineDiff,
}: {
  index: number;
  manifest: ManifestSection;
  box: WasteLineBoxNumbers;
  lineDiff: (reviewField: string) => ReviewFieldDiff | undefined;
}) {
  return (
    <>
      <SuggestedTextField
        name={`wasteLines.${index}.federalWasteCodes`}
        control={manifest.manifestControl}
        needsReview={manifest.manifestNeedsReview}
        label={`${box.wasteCodes}. Federal Waste Codes`}
        size="small"
        fullWidth
        helperText="Comma-separated"
        slotProps={{
          input: { endAdornment: reviewDiffAdornment(lineDiff("federalWasteCodes")) },
        }}
      />
      <SuggestedTextField
        name={`wasteLines.${index}.stateWasteCodes`}
        control={manifest.manifestControl}
        needsReview={manifest.manifestNeedsReview}
        label={`${box.wasteCodes}. State Waste Codes`}
        size="small"
        fullWidth
        helperText="Comma-separated"
        slotProps={{
          input: { endAdornment: reviewDiffAdornment(lineDiff("stateWasteCodes")) },
        }}
      />
      <SuggestedTextField
        name={`wasteLines.${index}.managementMethodCode`}
        control={manifest.manifestControl}
        needsReview={manifest.manifestNeedsReview}
        label={`${box.managementMethod}. Management Method Code`}
        size="small"
        fullWidth
        slotProps={{
          input: { endAdornment: reviewDiffAdornment(lineDiff("managementMethodCode")) },
        }}
      />
      <WasteLineTagsField index={index} manifest={manifest} />
    </>
  );
}

/**
 * Waste tags for one line. Not an EPA form field: clients with their own
 * shipment-tagging requirements had to run a spreadsheet import alongside
 * manifest capture just to attach these. Free-text entry creates the tag for
 * the manifest's tenant, matching the spreadsheet's "Waste Line Tags" column.
 *
 * Hidden on a tenantless manifest, where there's no tenant to scope tags to.
 */
function WasteLineTagsField({
  index,
  manifest,
}: {
  index: number;
  manifest: Pick<ManifestSection, "manifestControl" | "tenantId">;
}) {
  if (!manifest.tenantId) return null;

  return (
    <Controller
      name={`wasteLines.${index}.tags`}
      control={manifest.manifestControl}
      render={({ field }) => (
        <UnifiedTagsInput
          value={field.value}
          onChange={field.onChange}
          label="Tags"
          tagsQuery={TAGS_QUERY}
          queryConfig={{
            tenantId: manifest.tenantId ?? undefined,
            additionalSearch: "type:Waste isDisabled:false",
          }}
        />
      )}
    />
  );
}

function TransportersSection({
  manifest,
  rowsWithOffers,
}: {
  manifest: ManifestSection;
  rowsWithOffers: ReadonlySet<string>;
}) {
  const fieldArray = manifest.transporterFieldArray;

  // Each transporter is an independently collapsible accordion. Closed, the
  // summary shows name + EPA ID, so a reviewer with nothing to change reads the
  // carriers at a glance; open, it is a pair of fields to transcribe.
  const lineNumbers = useMemo(
    () => fieldArray.fields.map((f) => f.transporterLineNumber),
    [fieldArray.fields]
  );
  const { manifestNeedsReview } = manifest;
  const openByDefault = useCallback(
    (lineNumber: string) => manifestNeedsReview || rowsWithOffers.has(lineNumber),
    [manifestNeedsReview, rowsWithOffers]
  );
  const {
    isExpanded,
    setExpanded: setLineExpanded,
    allExpanded,
    toggleAll,
  } = useRowExpansion(lineNumbers, openByDefault);

  const handleAddTransporter = () => {
    const nextLineNumber = String(
      Math.max(0, ...fieldArray.fields.map((f) => safeParseInt(f.transporterLineNumber) ?? 0)) + 1
    );
    fieldArray.append({
      transporterLineNumber: nextLineNumber,
      transporterId: "",
      transporterName: "",
    });
    // Newly-added transporter starts expanded so the user can fill it in.
    setLineExpanded(nextLineNumber, true);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          6 &amp; 7. Transporters ({fieldArray.fields.length})
        </Typography>
        {fieldArray.fields.length > 1 && (
          <Button
            size="small"
            variant="text"
            startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Stack>
      {fieldArray.fields.map((field, index) => (
        <TransporterRow
          key={field.id}
          field={field}
          index={index}
          manifest={manifest}
          expanded={isExpanded(field.transporterLineNumber)}
          onToggle={(open) => setLineExpanded(field.transporterLineNumber, open)}
        />
      ))}
      <Button size="small" startIcon={<Add />} onClick={handleAddTransporter}>
        Add Transporter
      </Button>
    </>
  );
}

type TransporterRowProps = {
  field: { id: string; transporterLineNumber: string };
  index: number;
  manifest: ManifestSection;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
};

// EPA Form 8700-22 lists two transporters (Items 6 & 7); additional transporters
// go on the 8700-22A continuation sheet, two per sheet (Items 25 & 26).
const MAIN_FORM_TRANSPORTER_COUNT = 2;
function transporterBox(index: number): string {
  if (index === 0) return "6";
  if (index === 1) return "7";
  return (index - MAIN_FORM_TRANSPORTER_COUNT) % 2 === 0 ? "25" : "26";
}

function TransporterRow({ field, index, manifest, expanded, onToggle }: TransporterRowProps) {
  useRowReveal("transporters", index, onToggle);

  const epaId = useWatch({
    control: manifest.manifestControl,
    name: `transporters.${index}.transporterId`,
  });
  const name = useWatch({
    control: manifest.manifestControl,
    name: `transporters.${index}.transporterName`,
  });
  // Name first, then EPA ID, mirroring the manifest form's field order.
  const summary = [name, epaId].filter(Boolean).join(" · ");
  const box = transporterBox(index);

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => onToggle(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Transporter {field.transporterLineNumber}
          </Typography>
          {summary ? (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
              {" — "}
              {summary}
            </Typography>
          ) : (
            <Box flex={1} />
          )}
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              manifest.onDeleteTransporter(field.transporterLineNumber);
              manifest.transporterFieldArray.remove(index);
            }}
            title="Remove transporter"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <SuggestedTextField
            name={`transporters.${index}.transporterName`}
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label={`${box}. Company Name`}
            size="small"
            fullWidth
          />
          <Stack spacing={0.5}>
            <SuggestedTextField
              name={`transporters.${index}.transporterId`}
              control={manifest.manifestControl}
              needsReview={manifest.manifestNeedsReview}
              label={`${box}. EPA ID`}
              size="small"
              fullWidth
            />
            <EpaIdLookup epaId={epaId} />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// Box 18 is filled out by the receiving TSDF when there's a discrepancy
// between what the generator shipped and what arrived (corrected quantities,
// wrong waste type), or when material is partially/fully rejected, or when
// only a residue remains. Most manifests leave this blank — collapse by
// default unless one of the fields already has content (OCR or prior edit).
/** Box 18's own fields, for the reveal that opens the section before travel lands. */
const BOX_18_FIELDS: ReadonlySet<string> = new Set([
  "discrepancyComments",
  "residueComments",
  "rejectionComments",
]);

function Box18Section({ manifest }: { manifest: ManifestSection }) {
  // Box 18 shipped with the manifest-views-v2 surface; this dialog is also
  // reachable from the ungated customer Documents area, so gate the section on
  // the rollout flag (staff resolve it on via staff override).
  const showManifestViewsV2 = useManifestViewsV2();
  const box18Control = manifest.manifestControl;
  const discrepancyComments = useWatch({ control: box18Control, name: "discrepancyComments" });
  const residueComments = useWatch({ control: box18Control, name: "residueComments" });
  const rejectionComments = useWatch({ control: box18Control, name: "rejectionComments" });

  const filledCount = [discrepancyComments, residueComments, rejectionComments].filter(
    (v) => v && v.trim().length > 0
  ).length;

  // react-hook-form applies the `values` prop in a useEffect, so at mount the
  // watched fields read as empty even when OCR populated them. Storing
  // `expanded` from a lazy-init would freeze to false before hydration. Instead,
  // persist only the user's manual toggle and derive `expanded` live so it
  // tracks `filledCount` once values land.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  // Open on an unreviewed manifest for the same reason the repeated rows are:
  // a collapsed section's fields cannot take focus, so leaving this shut would
  // put three of them outside the transcription pass.
  const expanded = userExpanded ?? (manifest.manifestNeedsReview || filledCount > 0);

  // Named rather than prefixed: Box 18's three fields are top-level names with
  // nothing in common to match on.
  useRevealOnTravel(
    "box18",
    (path) => BOX_18_FIELDS.has(path),
    () => setUserExpanded(true)
  );

  if (!showManifestViewsV2) {
    return null;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isOpen) => setUserExpanded(isOpen)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flexShrink: 0 }}>
            18a. Discrepancy Indications
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
            {filledCount > 0
              ? // 0 is routed to "(none)" below, so the s/no-s ternary only handles 1 vs. ≥2.
                ` — ${filledCount} field${filledCount === 1 ? "" : "s"} populated`
              : "(none)"}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={3}>
          <Alert severity="info" variant="outlined">
            Box 18 is completed by the receiving facility (TSDF). On the paper form each row has a
            checkbox plus a narrative — capture the narrative in the matching field below; leave
            blank if the box is unchecked
          </Alert>
          <SuggestedTextField
            name="discrepancyComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Quantity / type discrepancy"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <SuggestedTextField
            name="residueComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Residue"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <SuggestedTextField
            name="rejectionComments"
            control={manifest.manifestControl}
            needsReview={manifest.manifestNeedsReview}
            label="18a. Rejection"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export const ManifestDetailSection = memo(ManifestDetailSectionInner);
